import React, { useCallback, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation, Pagination, Autoplay } from "swiper";

// Import Swiper styles (Swiper v9 không chia nhỏ theo file)
import "swiper/swiper-bundle.css";
import "./ProductSilder.module.scss";
import ProductItem from "../ProductItem/ProductItem";
import { translateProductName } from "../../utils/translateProductName";

// Kích hoạt module
SwiperCore.use([Navigation, Pagination, Autoplay]);

const ProductSilder = ({ items = [], prevRef, nextRef }) => {
  const swiperRef = useRef(null);
  const hasItems = items.length > 0;

  const bindSwiperControls = useCallback(
    (swiper) => {
      if (!swiper || swiper.destroyed || !prevRef?.current || !nextRef?.current) {
        return;
      }

      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;

      if (swiper.navigation) {
        swiper.navigation.destroy();
        swiper.navigation.init();
        swiper.navigation.update();
      }

      swiper.update();

      if (hasItems && swiper.autoplay && !swiper.autoplay.running) {
        swiper.autoplay.start();
      }
    },
    [hasItems, nextRef, prevRef]
  );

  useEffect(() => {
    if (!hasItems || !swiperRef.current) {
      return;
    }

    const timerId = window.setTimeout(() => {
      bindSwiperControls(swiperRef.current);
    }, 0);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [bindSwiperControls, hasItems, items.length]);

  if (!hasItems) {
    return null;
  }

  return (
    <Swiper
      spaceBetween={50}
      autoplay={{ delay: 3000, disableOnInteraction: false }}
      loop={true}
      navigation={{
        prevEl: prevRef?.current,
        nextEl: nextRef?.current,
      }}
      onBeforeInit={(swiper) => {
        // Gán ref trước khi Swiper khởi tạo để navigation hoạt động ngay
        if (prevRef?.current && nextRef?.current) {
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }
      }}
      onSwiper={(swiper) => {
        swiperRef.current = swiper;
        window.setTimeout(() => {
          bindSwiperControls(swiper);
        }, 0);
      }}
      breakpoints={{
        320: { slidesPerView: 2, spaceBetween: 20 },
        480: { slidesPerView: 2, spaceBetween: 25 },
        576: { slidesPerView: 2, spaceBetween: 30 },
        768: { slidesPerView: 3, spaceBetween: 35 },
        992: { slidesPerView: 4, spaceBetween: 40 },
        1200: { slidesPerView: 4, spaceBetween: 40 },
        1400: { slidesPerView: 4, spaceBetween: 50 },
        2000: { slidesPerView: 8, spaceBetween: 50 },
      }}
    >
      {items.map((item, index) => (
        <SwiperSlide key={index}>
          <ProductItem
            href={`/product-detail/${item.id}/${item.productText
              ?.split(" ")
              .join("-")}`}
            srcImage={item.srcImage}
            textType={item.textType}
            productText={translateProductName(item.productText)}
            price={item.price}
          />
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export default ProductSilder;
